import React, { Suspense, lazy } from 'react';
import { LoadingSpinner } from '../ui/loading-states';
import type { LocationMapRef } from './LocationMap';

// Ленивая загрузка карты для улучшения производительности
const LocationMap = lazy(() => import('./LocationMap'));

interface LazyLocationMapProps {
  latitude?: number;
  longitude?: number;
  radius?: number;
  isInteractive?: boolean;
  onLocationSelect?: (lat: number, lng: number) => void;
  onRadiusChange?: (radius: number) => void;
  height?: string;
  className?: string;
}

const LoadingFallback: React.FC = () => (
  <div className="h-full w-full flex items-center justify-center bg-gray-50 rounded-lg">
    <div className="flex flex-col items-center gap-3">
      <LoadingSpinner className="h-8 w-8" />
      <p className="text-sm text-gray-600">Загрузка карты...</p>
    </div>
  </div>
);

const ErrorFallback: React.FC<{ error: Error; resetError: () => void }> = ({ error, resetError }) => {
  const isChunkError = error.message.includes('dynamically imported module') || 
                      error.message.includes('Importing a module script failed');

  return (
    <div className="h-full w-full flex items-center justify-center bg-red-50 rounded-lg border border-red-200">
      <div className="text-center p-4">
        <p className="text-sm text-red-600 mb-2">
          {isChunkError ? 'Требуется обновление' : 'Ошибка загрузки карты'}
        </p>
        <p className="text-xs text-gray-500 mb-3">
          {isChunkError 
            ? 'Вышла новая версия приложения. Пожалуйста, перезагрузите страницу.' 
            : error.message}
        </p>
        <button
          onClick={isChunkError ? () => window.location.reload() : resetError}
          className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
        >
          {isChunkError ? 'Перезагрузить страницу' : 'Попробовать снова'}
        </button>
      </div>
    </div>
  );
};

// Error Boundary для обработки ошибок карты
class MapErrorBoundary extends React.Component<
  React.PropsWithChildren<{ onError?: (error: Error) => void }>,
  { hasError: boolean; error: Error | null }
> {
  constructor(props: React.PropsWithChildren<{ onError?: (error: Error) => void }>) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Map Error:', error, errorInfo);
    this.props.onError?.(error);
  }

  render() {
    if (this.state.hasError && this.state.error) {
      return (
        <ErrorFallback
          error={this.state.error}
          resetError={() => this.setState({ hasError: false, error: null })}
        />
      );
    }

    return this.props.children;
  }
}

const LazyLocationMap = React.forwardRef<LocationMapRef, LazyLocationMapProps>((props, ref) => {
  return (
    <MapErrorBoundary>
      <Suspense fallback={<LoadingFallback />}>
        <LocationMap ref={ref} {...props} />
      </Suspense>
    </MapErrorBoundary>
  );
});

LazyLocationMap.displayName = 'LazyLocationMap';

export default LazyLocationMap;